// Loading Page Component - Modern & Tech Version
import React from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';
import ArsenalLogo from './ArsenalLogo';

const LoadingPage = ({ message = 'Cargando...', size = 60, showLogo = true }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background grid */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(${alpha(theme.palette.primary.main, 0.03)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.03)} 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          animation: 'grid-move 20s linear infinite',
        }}
      />

      {/* Animated circles in background */}
      <Box
        sx={{
          position: 'absolute',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 70%)`,
          animation: 'pulse-slow 4s ease-in-out infinite',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          animation: 'pulse-slow 4s ease-in-out infinite 1s',
        }}
      />

      {/* Main content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
        }}
      >
        {/* Logo with glow effect */}
        {showLogo && (
          <Box
            sx={{
              position: 'relative',
              animation: 'float 3s ease-in-out infinite',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: -10,
                background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.2)} 0%, transparent 70%)`,
                borderRadius: '50%',
                filter: 'blur(20px)',
                animation: 'glow 2s ease-in-out infinite',
              }}
            />
            <ArsenalLogo
              sx={{
                width: 80,
                height: 80,
                color: theme.palette.primary.main,
                position: 'relative',
                zIndex: 1,
                filter: 'drop-shadow(0 0 10px rgba(0,0,0,0.1))',
              }}
            />
          </Box>
        )}

        {/* Modern circular loader */}
        <Box sx={{ position: 'relative', width: size + 40, height: size + 40 }}>
          {/* Outer rotating ring */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          />

          {/* Animated segments */}
          {[0, 1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: `2px solid transparent`,
                borderTopColor: theme.palette.primary.main,
                animation: `spin 1.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite`,
                animationDelay: `${i * 0.15}s`,
                opacity: 1 - (i * 0.2),
              }}
            />
          ))}

          {/* Inner pulsing circle */}
          <Box
            sx={{
              position: 'absolute',
              inset: '30%',
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              animation: 'pulse-inner 2s ease-in-out infinite',
            }}
          />

          {/* Center dot */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: theme.palette.primary.main,
              transform: 'translate(-50%, -50%)',
              boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.5)}`,
            }}
          />
        </Box>

        {/* Loading text with shimmer effect */}
        <Box sx={{ position: 'relative', overflow: 'hidden' }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              textAlign: 'center',
              color: 'text.secondary',
              letterSpacing: '0.5px',
            }}
          >
            {message}
          </Typography>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.2)}, transparent)`,
              animation: 'shimmer 2s infinite',
            }}
          />
        </Box>

        {/* Progress dots - more tech style */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Box
              key={i}
              sx={{
                width: i === 2 ? 4 : 3,
                height: i === 2 ? 12 : 8,
                borderRadius: 1,
                bgcolor: theme.palette.primary.main,
                animation: 'wave 1.5s ease-in-out infinite',
                animationDelay: `${i * 0.1}s`,
                opacity: 0.3,
              }}
            />
          ))}
        </Box>

        {/* Scanning line effect */}
        <Box
          sx={{
            width: 200,
            height: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            borderRadius: 1,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '30%',
              height: '100%',
              bgcolor: theme.palette.primary.main,
              borderRadius: 1,
              animation: 'scan 2s ease-in-out infinite',
              boxShadow: `0 0 10px ${theme.palette.primary.main}`,
            }}
          />
        </Box>
      </Box>

      {/* Keyframe animations */}
      <style>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @keyframes pulse-slow {
          0%, 100% {
            transform: scale(1);
            opacity: 0.3;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.6;
          }
        }

        @keyframes pulse-inner {
          0%, 100% {
            transform: scale(1);
            opacity: 0.2;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.4;
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes glow {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
        }

        @keyframes wave {
          0%, 100% {
            transform: scaleY(0.5);
            opacity: 0.3;
          }
          50% {
            transform: scaleY(1);
            opacity: 1;
          }
        }

        @keyframes shimmer {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }

        @keyframes scan {
          0% {
            left: -30%;
          }
          100% {
            left: 100%;
          }
        }

        @keyframes grid-move {
          0% {
            transform: translate(0, 0);
          }
          100% {
            transform: translate(50px, 50px);
          }
        }
      `}</style>
    </Box>
  );
};

export default LoadingPage;